import { ReloadOutlined } from "@ant-design/icons";
import { Button, Input, message, Space, Spin, Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { fmtDateTimeFull } from "@extensions/utils";
import CompanyItem from "./company-item";
import { fmtDate } from "@extensions/convert";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "./companies.css";

/** Дата подписки для карточки: текст и стиль (истёкшая / действующая). Нет даты — null. */
const getSubscriptionLine = raw => {
  if (raw == null || raw === "") return null;
  const d = dayjs(raw);
  if (!d.isValid()) return null;
  const text = fmtDate(raw);
  if (!text) return null;
  const today = dayjs().startOf("day");
  const subDay = d.startOf("day");
  const muted = subDay.isBefore(today);
  return { text, muted };
};

const Companies = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);

  const getData = () => {
    setLoading(true);
    
    const url = searchText 
      ? `company/search?searchText=${encodeURIComponent(searchText)}`
      : `company/search`;
    
    serverFetch(url)
      .then((data) => {
        setLoading(false);
        setItems(data);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearch = () => {
    getData();
  };

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const onRowClick = (record) => {
    setSelectedCompany({
      companyId: record.id,
    });
  };

  const handleCompanyClose = (refresh) => {
    setSelectedCompany(null);
    if (refresh) {
      getData();
    }
  };

  const formatCompanyMetaLine = record => {
    const parts = [];
    if (record.id != null && record.id !== "") parts.push(`#${record.id}`);
    const email = record.email != null ? String(record.email).trim() : "";
    const phone = record.phone != null ? String(record.phone).trim() : "";
    const inn = record.inn != null ? String(record.inn).trim() : "";
    if (email) parts.push(email);
    if (phone) parts.push(phone);
    if (inn) parts.push(inn);
    return parts.length ? parts.join(" · ") : "—";
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 60 },
    { dataIndex: "name", title: "Название" },
    { dataIndex: "inn", title: "ИНН" },
    { dataIndex: "email", title: "Email" },
    { dataIndex: "phone", title: "Телефон" },
    { dataIndex: "regDateTime", title: "Создана", render: fmtDateTimeFull },
    { dataIndex: "supportFinishDate", title: "Подписка", render: fmtDate },
  ];

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="companies-mobile-list">
        {items.map(record => {
          const subLine = getSubscriptionLine(record.supportFinishDate);
          return (
            <div
              key={record.id}
              role="button"
              tabIndex={0}
              className="companies-mobile-card"
              onClick={() => onRowClick(record)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(record);
                }
              }}
            >
              {record.name != null && String(record.name).trim() !== "" ? (
                <div className="companies-mobile-card-name">{record.name}</div>
              ) : null}
              <div className="companies-mobile-card-meta">{formatCompanyMetaLine(record)}</div>
              <div className="companies-mobile-card-dates">
                <span className="companies-mobile-card-date-created">{fmtDateTimeFull(record.regDateTime) || "—"}</span>
                {subLine ? (
                  <span
                    className={
                      subLine.muted
                        ? "companies-mobile-card-date-sub companies-mobile-card-date-sub--muted"
                        : "companies-mobile-card-date-sub companies-mobile-card-date-sub--active"
                    }
                  >
                    {subLine.text}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  const tableEl = (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="id"
      pagination={false}
      size="small"
      onRow={record => ({
        onClick: () => onRowClick(record),
        style: { cursor: "pointer" },
      })}
    />
  );

  return (
    <div className={isMobile ? "page orders-page--mobile" : "page"}>
      {isMobile ? (
        <div className="orders-mobile-toolbar">
          <div className="company-backups-mobile-search-row" style={{ marginBottom: 0 }}>
            <Input
              placeholder="Поиск компаний..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              allowClear
              className="company-backups-mobile-search-input"
            />
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={handleSearch}
              loading={loading}
              aria-label="Обновить"
              className="company-backups-mobile-refresh-btn"
            />
          </div>
        </div>
      ) : (
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Поиск компаний..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{ width: 300 }}
          />
          <Button type="primary" onClick={handleSearch} loading={loading}>
            Поиск
          </Button>
        </Space>
      )}

      {isMobile ? <div className="orders-mobile-scroll">{mobileListEl}</div> : tableEl}
      
      {selectedCompany && (
        <CompanyItem
          companyId={selectedCompany.companyId}
          onClose={handleCompanyClose}
        />
      )}
    </div>
  );
};

export default Companies;